import { memo, useMemo } from 'react';
import Button from '@/components/atoms/Button/Button';
import usePublishAsset from '@/hooks/usePublishAsset';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useClipPendingUpdate } from '@/hooks/useClipPendingUpdate';
import { ClipSyncStatus } from '@/stores/pendingEditsStore';
import Icon from '@/components/atoms/Icon';

function ClipPublishToLibraryDialog({
  onClose,
  isClipAddedToLibrary
}: {
  onClose: () => void;
  isClipAddedToLibrary: boolean;
}) {
  const { publishAsset, isPublishing } = usePublishAsset();
  const { clipData } = useClipsContext();

  const { status: clipStatus } = useClipPendingUpdate({ clipId: clipData.id });

  const handlePublish = async () => {
    await publishAsset();
    onClose();
  };

  const translatedLangs = useMemo(() => {
    if (!clipData?.asset_metadata?.translated_langs) {
      return [];
    }
    const originalLang = clipData?.asset_metadata?.original_lang;
    return clipData?.asset_metadata?.translated_langs.filter(lang => lang.code !== originalLang);
  }, [clipData]);

  const isPublishDisabled = useMemo(() => {
    return isPublishing || clipStatus !== ClipSyncStatus.sync_completed;
  }, [isPublishing, clipStatus]);

  const publishButtonCopy = useMemo(() => {
    if (clipStatus === ClipSyncStatus.sync_in_progress) {
      return 'Saving changes...';
    }
    if (clipStatus === ClipSyncStatus.sync_failed) {
      return 'Changes not saved. Please try again.';
    }
    if (isClipAddedToLibrary) {
      return 'Publish and update';
    }
    return 'Publish changes';
  }, [clipStatus, isClipAddedToLibrary]);

  return (
    <div className="relative flex flex-col overflow-y-auto overflow-x-hidden">
      <div className="grow px-5 pb-5">
        <div className="grow pt-0.5">
          <h1 className="font-medium">{isClipAddedToLibrary ? 'Publish' : 'Publish to a new destination'}</h1>
          <div className="mt-1.5 w-full text-sm leading-tight text-slate-600">
            {!isClipAddedToLibrary
              ? `This video is currently not in use anywhere. Click publish to add it to Video Library and easily use it
            later in any of the following destinations.`
              : 'This video is currently not in use anywhere. Click publish to sync changes to Video Library and easily use it later in any of the following destinations.'}
          </div>

          {translatedLangs.length > 0 && (
            <div className="mt-5 flex text-sm leading-tight text-slate-600">
              Subtitles available in:
              <div className="ml-1 flex flex-wrap items-center gap-1">
                {translatedLangs?.map((lang, index) => (
                  <div key={lang.code} className="text-sm leading-tight">
                    {lang.name}
                    {index < translatedLangs.length - 1 && ', '}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5">
            <div className="group flex w-full items-center space-x-3 rounded-lg p-2 py-3 pl-3 text-left hover:bg-slate-100">
              <Icon name="IconCalendar" />
              <div className="grow truncate">
                <div className="text-sm font-medium leading-tight">Webinar</div>
                <div className="mt-0.5 truncate text-xs leading-tight text-slate-600">
                  Single session with post-event AI content
                </div>
              </div>
            </div>
            <div className="group flex w-full items-center space-x-3 rounded-lg p-2 py-3 pl-3 text-left hover:bg-slate-100">
              <Icon name="IconCalendarEvent" />
              <div className="grow truncate">
                <div className="text-sm font-medium leading-tight">Event</div>
                <div className="mt-0.5 truncate text-xs leading-tight text-slate-600">
                  Multiple sessions with breakout spaces
                </div>
              </div>
            </div>
            <div className="group flex w-full items-center space-x-3 rounded-lg p-2 py-3 pl-3 text-left hover:bg-slate-100">
              <Icon name="IconBrandYoutube" />
              <div className="grow truncate">
                <div className="flex items-center">
                  <div className="text-sm font-medium leading-tight">Pages</div>
                </div>
                <div className="mt-0.5 truncate text-xs leading-tight text-slate-600">
                  Customizable Pages with videos or event
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 border-t border-t-slate-200 px-5 pb-3 pt-2.5">
        <Button variation="outline" trackingId="cancel-publishing" onClick={onClose}>
          Cancel
        </Button>
        <Button variation="filled" trackingId="publish-content" disabled={isPublishDisabled} onClick={handlePublish}>
          {publishButtonCopy}
          {isPublishing && <Icon name="IconLoader2" className="ml-2 animate-spin" size={16} />}
        </Button>
      </div>
    </div>
  );
}

export default memo(ClipPublishToLibraryDialog);
